import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import { FlexColumnSpaced } from '@fox/layout/src/flex';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, PaymentIntentResult } from '@stripe/stripe-js';
import { useImperativeHandle, useState } from 'react';
import { DESIGN_MODE_INTENT } from '../../../constants';
import { PaymentAddComponentProps } from '../../../contracts';
import { useFormPaymentContext } from '../../form-payment-context';

/**
 * Component to use the stripe PaymentForm for payments.
 * Google Pay and Apple Pay should be detected and be an option when the user has an available wallet.
 * The intent needs the amount (otherwise the google iframe shows $0.00) so we need to update the intent with the correct amount before submitting.
 * Completing intent creates a credit card in fincancial backend that cannot be charged and contains information for processing and veryfying the receipt.
 */
function StripePaymentForm({ onSuccess, updateIntent, completeIntent, intentData }: PaymentAddComponentProps) {
	const stripe = useStripe();
	const elements = useElements();
	const tempData = useTempDataContext();
	const { grandTotal } = useFormPaymentContext();
	const total = grandTotal;
	const onSubmit = async event => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		event?.preventDefault();
		if (!stripe || !elements) {
			// Stripe.js has not yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return;
		}
		if (updateIntent && total > 100) {
			await updateIntent(intentData.intentId, total);
			await elements?.fetchUpdates();

			//this does the actual payment
			const res: PaymentIntentResult = await stripe.confirmPayment({
				elements,
				confirmParams: { return_url: window.location.href },
				redirect: 'if_required'
			});
			//if the payment fails, stop it, get some help
			if (res.error) {
				throw new Error('Failed to confirm card details');
			}

			const acres = await completeIntent(
				intentData.intentId,
				JSON.stringify({
					paymentMethodId:
						typeof res.paymentIntent.payment_method === 'string'
							? res.paymentIntent.payment_method
							: res.paymentIntent.payment_method?.id,
					amount: res.paymentIntent.amount,
					status: res.paymentIntent.status,
					currency: res.paymentIntent.currency,
					createdOn: new Date(res.paymentIntent.created * 1000),
					intentId: intentData.intentId
				})
			);
			// We assume success if we get to this point
			if (tempData) tempData.cardFormDataValid = true;

			//we should still just get a card id here
			onSuccess(acres);
		}
	};

	useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: onSubmit
		})
	);

	return (
		<form onSubmit={onSubmit}>
			<FlexColumnSpaced>
				<PaymentElement
					onChange={e => {
						if (tempData) {
							tempData.cardFormDataValid = e.complete;
						}
					}}
					options={{ wallets: { applePay: 'auto', googlePay: 'auto' } }}
				/>
			</FlexColumnSpaced>
		</form>
	);
}

type StripeFormWrapperProps = PaymentAddComponentProps & {
	pubKey: string;
};

function StripeFormWrapper({ pubKey, ...rest }: StripeFormWrapperProps) {
	const clientSecret = rest?.intentData?.key;
	const [stripePromise] = useState(() => loadStripe(pubKey));
	const options = {
		clientSecret
	};
	if (!clientSecret && rest.intentData?.error) {
		return null;
	}

	if (clientSecret === DESIGN_MODE_INTENT.key) {
		return <MuiAlert severity="info">Stripe Payment</MuiAlert>;
	}

	return (
		<Elements stripe={stripePromise} options={options}>
			<StripePaymentForm {...rest} />
		</Elements>
	);
}

export function StripeOneTimePayment(props: PaymentAddComponentProps) {
	var publishableKey = props.settings?.['publishableKey'];

	return <StripeFormWrapper pubKey={publishableKey} {...props} />;
}
